import { Box, IconButton, Stack, Switch, Typography } from '@mui/material';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import { useRef, useEffect, forwardRef, useImperativeHandle, useState, SyntheticEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import SignaturePad from 'signature_pad';
import { CANVAS_BORDERS_WIDTH, CANVAS_HEIGHT, PEN_WIDTH } from './constants';
import { useGetProfileQuery } from '../profile/api/profileApi';
import { generateSignatureText } from '../utils';

interface ESignatureProps {
    setIsSignatureEmpty: (isEmpty: boolean) => void;
}

export interface ESignatureHandle {
    getSignatureBlob: () => Promise<Blob | null>;
}

const ESignature = forwardRef<ESignatureHandle, ESignatureProps>(({ setIsSignatureEmpty }, ref) => {
    const { t } = useTranslation();
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const signaturePadRef = useRef<SignaturePad | null>(null);
    const [isAutoGenerate, setIsAutoGenerate] = useState(false);
    const { data: user } = useGetProfileQuery();

    const clearSignature = useCallback(() => {
        signaturePadRef.current?.clear();
        setIsSignatureEmpty(true);
    }, [setIsSignatureEmpty]);

    useEffect(() => {
        const resizeCanvas = () => {
            const canvas = canvasRef.current;
            const parent = canvas?.parentElement;
            if (!parent) return;
            setIsAutoGenerate(false);
            clearSignature();
            canvas.width = parent.clientWidth - CANVAS_BORDERS_WIDTH;
        };

        const handleBeginStroke = () => {
            setIsSignatureEmpty(false);
        };

        if (canvasRef.current) {
            signaturePadRef.current = new SignaturePad(canvasRef.current, {
                penColor: '#000',
                maxWidth: PEN_WIDTH,
            });

            resizeCanvas();

            window.addEventListener('resize', resizeCanvas);
            signaturePadRef.current.addEventListener('beginStroke', handleBeginStroke);

            return () => {
                signaturePadRef.current?.removeEventListener('beginStroke', handleBeginStroke);
                window.removeEventListener('resize', resizeCanvas);
            };
        }
    }, [setIsSignatureEmpty, clearSignature]);

    useImperativeHandle(
        ref,
        () => ({
            getSignatureBlob: async () => {
                return new Promise<Blob | null>((resolve) => {
                    canvasRef.current?.toBlob((blob) => {
                        resolve(blob);
                    }, 'image/png');
                });
            },
        }),
        []
    );

    const handleAutoGenerate = (_: SyntheticEvent, checked: boolean) => {
        setIsAutoGenerate(checked);
        if (checked) {
            generateSignature();
        } else {
            clearSignature();
        }
    };

    const generateSignature = async () => {
        const ctx = canvasRef.current?.getContext('2d');
        if (!ctx || !canvasRef.current || !user) return;

        signaturePadRef.current?.clear();
        await generateSignatureText({
            ctx,
            name: `${user.firstName} ${user.lastName}`,
            width: canvasRef.current.width,
            height: canvasRef.current.height,
        });
        setIsSignatureEmpty(false);
    };

    return (
        <Box>
            <Stack direction='row' justifyContent='space-between' alignItems='center'>
                <Typography variant='subtitle1' color='common.black'>
                    {t('e-signature.create-from-name')}
                </Typography>
                <Switch
                    sx={{
                        marginRight: '-10px',
                        '& .MuiSwitch-switchBase': {
                            '&.Mui-checked': {
                                color: '#fff',
                                '& + .MuiSwitch-track': {
                                    backgroundColor: 'common.themes.blue.primary',
                                    opacity: 1,
                                },
                            },
                        },
                    }}
                    onChange={handleAutoGenerate}
                    checked={isAutoGenerate}
                />
            </Stack>
            <Stack direction='row' justifyContent='space-between' alignItems='center'>
                <Typography variant='subtitle1' color='common.black'>
                    {t('e-signature.sign-below')}
                </Typography>
                <IconButton
                    aria-label='reset'
                    onClick={clearSignature}
                    disabled={isAutoGenerate}
                    sx={{ color: 'common.grey1', marginRight: '-10px' }}
                >
                    <RefreshOutlinedIcon />
                </IconButton>
            </Stack>
            <canvas
                ref={canvasRef}
                height={CANVAS_HEIGHT}
                style={{
                    display: 'block',
                    border: '1px solid #CFCFCF',
                    borderRadius: '4px',
                    pointerEvents: isAutoGenerate ? 'none' : 'auto',
                }}
            ></canvas>
        </Box>
    );
});

export default ESignature;
